import { memo, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  trackCompareGridEvents,
  CompareGridLocations,
  CompareGridEventNames,
} from '@utils/gtm/ga4/trackCompareGridEvents';
import setCompareTableClickCookie from '@utils/gtm/ga4/setCompareTableClickCookie';
import { useTranslation } from 'next-i18next';
import { useLocale } from '@hooks/useLocale';
import { ArrowLeftIcon, ArrowRightIcon } from '@phosphor-icons/react';
import DOMPurify from 'isomorphic-dompurify';

import IconButton from '_components/UI/IconButton';
import { MecProduct, PriceGroup } from '@adapters/bigcommerce/types/product';
import { MultiGroupPrices } from '@adapters/bigcommerce/operations/getAllCustomerGroupPrices';
import useProduct from '@adapters/bigcommerce/hooks/product/useProduct';
import { transformProductPrice } from '@utils/comparison/transformProductPrice';
import {
  getAttributeType,
  getTechSpecValue,
  getTechSpecArray,
  formatList,
} from '@utils/comparison/getCompareAttribute';
import { usePrice } from '@hooks/usePrice';
import { slugify } from '@utils/url';

import ProductColumn from './columns/ProductColumn';
import LoadingSkeleton from './LoadingSkeleton';
import { CompareAttribute, ProductCompareTableProps } from './types';

import styles from './ProductCompareTable.module.scss';

const FIXED_ATTRS: CompareAttribute[] = [
  { key: 'name', label: { en: 'Name', fr: 'Nom' } },
  { key: 'price', label: { en: 'Price', fr: 'Prix' } },
];

const ProductCompareTable = (props: ProductCompareTableProps) => {
  const { t } = useTranslation('comparison');
  const locale = useLocale();
  const formatPrice = usePrice();
  const { getProduct } = useProduct();
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);
  const hasTrackedScroll = useRef(false);
  const [mecProducts, setMecProducts] = useState<MecProduct[]>([]);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    const handleScroll = () => {
      updateScrollState();
      if (!hasTrackedScroll.current) {
        hasTrackedScroll.current = true;
        trackCompareGridEvents({
          eventName: CompareGridEventNames.ScrollCompareTable,
          interactionLocation: props.isFranchise
            ? CompareGridLocations.FranchiseCompare
            : CompareGridLocations.ProductCompare,
        });
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      ro.disconnect();
    };
  }, [updateScrollState, mecProducts, props.isFranchise]);

  const handleSeedNameClick = useCallback(
    (
      e: React.MouseEvent<HTMLAnchorElement>,
      linkName: string,
      href: string,
      index: number
    ) => {
      const atc = document.getElementById('add-to-cart');
      if (!atc) return;
      trackCompareGridEvents({
        eventName: CompareGridEventNames.ClickCompareTable,
        interactionLocation: isFranchise
          ? CompareGridLocations.FranchiseCompare
          : CompareGridLocations.ProductCompare,
        linkUrl: href,
        linkText: linkName,
        index: index,
        path: window.location.pathname,
      });
      e.preventDefault();
      atc.scrollIntoView({ behavior: 'smooth', block: 'end' });
    },
    [props.isFranchise]
  );

  const scrollByColumn = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    const table = tableRef.current;
    if (!el) return;
    const firstProductHeader = table?.querySelector<HTMLElement>(
      'thead tr th:nth-child(2)'
    );
    const colWidth = firstProductHeader?.offsetWidth ?? 304;
    el.scrollBy({
      left: direction === 'left' ? -colWidth : colWidth,
      behavior: 'smooth',
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableElement>) => {
      const table = tableRef.current;
      if (!table) return;

      const focusable = Array.from(
        table.querySelectorAll<HTMLElement>(
          'td[tabindex="0"], th[tabindex="0"], a, button, [tabindex="0"]'
        )
      );
      const idx = focusable.findIndex((el) => el === document.activeElement);
      if (idx === -1) return;

      let next = idx;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          next = Math.min(idx + 1, focusable.length - 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          next = Math.max(idx - 1, 0);
          break;
        case 'ArrowDown':
        case 'ArrowUp': {
          e.preventDefault();
          const cell = focusable[idx];
          const row = cell.closest('tr');
          if (!row) return;
          const allRows = Array.from(
            table.querySelectorAll('tbody tr, thead tr')
          );
          const rowIdx = allRows.indexOf(row);
          const delta = e.key === 'ArrowDown' ? 1 : -1;
          const nextRow = allRows[rowIdx + delta];
          if (nextRow) {
            const colIdx = Array.from(row.children).indexOf(
              cell as HTMLElement
            );
            const nextCell = nextRow.children[colIdx] as HTMLElement;
            if (nextCell) {
              const focusableInCell =
                nextCell.querySelector<HTMLElement>(
                  'a, button, [tabindex="0"]'
                ) ?? (nextCell.hasAttribute('tabindex') ? nextCell : null);
              focusableInCell?.focus();
            }
          }
          return;
        }
        case 'Home':
          e.preventDefault();
          next = 0;
          break;
        case 'End':
          e.preventDefault();
          next = focusable.length - 1;
          break;
        default:
          return;
      }

      if (next !== idx) focusable[next]?.focus();
    },
    []
  );

  const { isFranchise, product, config, groupPricesByCode } = props;

  const handleTableClick = ({
    linkName,
    href,
    index,
  }: {
    linkName: string;
    href: string;
    index: number;
  }) => {
    setCompareTableClickCookie({
      eventName: CompareGridEventNames.ClickCompareTable,
      interactionLocation: isFranchise
        ? CompareGridLocations.FranchiseCompare
        : CompareGridLocations.ProductCompare,
      linkUrl: href,
      linkText: linkName,
      index,
      path: window.location.pathname,
    });
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true;
          trackCompareGridEvents({
            eventName: CompareGridEventNames.ViewCompareTable,
            interactionLocation: isFranchise
              ? CompareGridLocations.FranchiseCompare
              : CompareGridLocations.ProductCompare,
          });
          observer.disconnect();
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // mecProducts in deps ensures the effect re-runs once the container div
    // replaces the loading skeleton (containerRef.current is null before that)
  }, [isFranchise, mecProducts]);

  const productCodes = useMemo(
    () => [product.code, ...config.compareProductIds],

    [product.code, config.compareProductIds]
  );

  useEffect(() => {
    let cancelled = false;
    const fetchProducts = async () => {
      const fetched = await Promise.all(
        productCodes.map((code) => (code ? getProduct(code) : null))
      );
      if (!cancelled) {
        setMecProducts(fetched.filter((p): p is MecProduct => p !== null));
      }
    };
    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [getProduct, productCodes]);

  const groupPricesMap = useMemo(() => {
    const map = new Map<string, MultiGroupPrices | null>();
    if (groupPricesByCode) {
      Object.entries(groupPricesByCode).forEach(([code, prices]) => {
        map.set(code, prices);
      });
    }
    return map;
  }, [groupPricesByCode]);

  const productPrices = useMemo(() => {
    const map = new Map<string, PriceGroup | null>();
    mecProducts.forEach((p) => {
      if (p?.code) map.set(p.code, transformProductPrice(p));
    });
    return map;
  }, [mecProducts]);

  const sortedAttributes = useMemo(() => {
    const rest = config.attributes.filter((a) => {
      const key = a.label[locale as 'en' | 'fr'] ?? a.key;
      const type = getAttributeType(key);
      return type !== 'name' && type !== 'price';
    });
    return [...FIXED_ATTRS, ...rest];
  }, [config.attributes, locale]);

  if (mecProducts.length === 0) {
    return <LoadingSkeleton />;
  }

  return (
    <div ref={containerRef} className={styles.container}>
      <nav className={styles.navigation} aria-label={t('compare.nav.label')}>
        <IconButton
          icon={ArrowLeftIcon}
          type={canScrollLeft ? 'primary' : 'outline'}
          size="md"
          iconSize={24}
          iconWeight="light"
          shape="circular"
          disabled={!canScrollLeft}
          aria-label={t('compare.nav.previous')}
          onClick={() => scrollByColumn('left')}
        />
        <IconButton
          icon={ArrowRightIcon}
          type={canScrollRight ? 'primary' : 'outline'}
          size="md"
          iconSize={24}
          iconWeight="light"
          shape="circular"
          disabled={!canScrollRight}
          aria-label={t('compare.nav.next')}
          onClick={() => scrollByColumn('right')}
        />
      </nav>

      {config.heading?.line1 && (
        <h3 className={styles.headingMobile}>{config.heading.line1}</h3>
      )}

      <div ref={scrollRef} className={styles.scrollWrapper}>
        <table
          ref={tableRef}
          className={styles.table}
          aria-label={t('title')}
          role="grid"
          onKeyDown={handleKeyDown}
        >
          <caption className={styles.srOnly}>{t('title')}</caption>
          <colgroup>
            <col className={styles.labelCol} />
            {mecProducts.map((_, i) => (
              <col
                key={i}
                className={`${styles.productCol} ${
                  i === 0 ? styles.firstProductCol : ''
                }`}
              />
            ))}
          </colgroup>

          <thead>
            <tr>
              <th
                scope="col"
                className={`${styles.labelCell} ${styles.labelHeaderCell}`}
              >
                {config.heading?.line1 ? (
                  <h3 className={styles.heading}>{config.heading.line1}</h3>
                ) : (
                  <span className={styles.srOnly}>
                    {t('compare.attributes')}
                  </span>
                )}
              </th>
              {mecProducts.map((p, i) => {
                const isPdpProduct = i === 0;
                const productHref = isPdpProduct
                  ? '#add-to-cart'
                  : `/${locale ?? 'en'}/product/${p.code}/${slugify(p.slug)}`;
                return (
                  <th
                    key={p.code}
                    scope="col"
                    className={`${styles.productHeaderCell} ${
                      !isFranchise && isPdpProduct ? styles.pdpColumn : ''
                    }`}
                  >
                    <ProductColumn
                      product={p}
                      href={productHref}
                      onClick={
                        isPdpProduct
                          ? (e) =>
                              handleSeedNameClick(e, p.name, productHref, i)
                          : () =>
                              handleTableClick({
                                linkName: p.name,
                                href: productHref,
                                index: i,
                              })
                      }
                      ariaLabel={p.name}
                      isFirst={isPdpProduct}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {sortedAttributes.map((attr, attrIdx) => {
              const specKey = attr.label[locale as 'en' | 'fr'] ?? attr.key;
              const type = getAttributeType(specKey);
              const isLastAttr = attrIdx === sortedAttributes.length - 1;

              return (
                <tr
                  key={attr.key}
                  className={`${styles.attrRow}${
                    isLastAttr ? ` ${styles.attrRowLast}` : ''
                  }`}
                >
                  <th scope="row" className={styles.labelCell}>
                    {attr.label[locale as 'en' | 'fr'] ?? attr.key}
                  </th>
                  {mecProducts.map((p, i) => {
                    const isPdpProduct = i === 0;
                    let content: React.ReactNode;

                    if (type === 'name') {
                      const idealForConjunction = t('compare.ideal.for.and');
                      const idealForItems = getTechSpecArray(
                        p,
                        locale === 'en' ? 'Ideal for' : 'Idéal pour'
                      );
                      const idealForSplit = idealForItems?.flatMap((item) =>
                        item.split(new RegExp(` ${idealForConjunction} `, 'i'))
                      );
                      const idealFor = idealForSplit?.length
                        ? formatList(idealForSplit, idealForConjunction)
                        : null;
                      const nameHref = isPdpProduct
                        ? '#add-to-cart'
                        : `/${locale ?? 'en'}/product/${p.code}/${slugify(
                            p.slug
                          )}`;
                      content = (
                        <div className={styles.nameWrapper}>
                          <a
                            href={nameHref}
                            className={styles.productNameLink}
                            onClick={
                              isPdpProduct
                                ? (e) =>
                                    handleSeedNameClick(e, p.name, nameHref, i)
                                : () =>
                                    handleTableClick({
                                      linkName: p.name,
                                      href: nameHref,
                                      index: i,
                                    })
                            }
                          >
                            <span className={styles.productName}>{p.name}</span>
                          </a>
                          {idealFor && (
                            <span className={styles.idealFor}>
                              {t('compare.ideal.for')}{' '}
                              {idealFor.toLocaleLowerCase(locale)}
                            </span>
                          )}
                        </div>
                      );
                    } else if (type === 'price') {
                      const price = productPrices.get(p.code ?? '');
                      const exactPrice = price?.price?.value ?? null;
                      const lowPriceValue = price?.lowPrice?.value ?? null;
                      const formatted =
                        exactPrice != null
                          ? formatPrice(exactPrice)
                          : lowPriceValue != null
                          ? formatPrice(lowPriceValue)
                          : null;
                      const groupPrices = groupPricesMap.get(p.code ?? '');
                      const guestLow =
                        groupPrices?.guest?.price?.lowPrice?.value;
                      const memberLow =
                        groupPrices?.member?.price?.lowPrice?.value;
                      const memberPrice =
                        guestLow != null &&
                        memberLow != null &&
                        guestLow > memberLow
                          ? guestLow - memberLow
                          : null;

                      content = formatted ? (
                        <>
                          <span>
                            {exactPrice != null
                              ? formatted
                              : t('compare.from.price', { price: formatted })}
                          </span>
                          {memberPrice && (
                            <span className={styles.memberSavings}>
                              {t('compare.members.save')}
                            </span>
                          )}
                        </>
                      ) : (
                        <span>{t('compare.placeholder')}</span>
                      );
                    } else {
                      const val = getTechSpecValue(p, specKey);
                      content = val ? (
                        <span
                          className={styles.attrValue}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(val),
                          }}
                        />
                      ) : (
                        <span className={styles.placeholder}>
                          {t('compare.placeholder')}
                        </span>
                      );
                    }

                    return (
                      <td
                        key={`${p.code}-${attr.key}`}
                        className={`${styles.dataCell} ${
                          !isFranchise && isPdpProduct ? styles.pdpColumn : ''
                        }`}
                        tabIndex={0}
                        role="gridcell"
                      >
                        {type !== 'name' && (
                          <span className={styles.attrLabelMobile}>
                            {attr.label[locale as 'en' | 'fr'] ?? attr.key}
                          </span>
                        )}
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default memo(ProductCompareTable);
